import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static targets = [
    "query",
    "type",
    "difficulty",
    "muscle",
    "equipment",
    "results",
    "item",
    "empty",
    "count",
    "status",
    "input",
    "selectedName",
    "selectedDetails",
    "submit"
  ]

  static values = {
    url: String
  }

  connect() {
    this.activeScope = null
    this.activeScopeId = null
    this.loaded = false
    this.debounceTimer = null
    this.abortController = null
    this.returnModalElement = null
    this.returnAfterPickerHidden = null
    this.refreshSelectionState()
  }

  disconnect() {
    window.clearTimeout(this.debounceTimer)
    if (this.abortController) this.abortController.abort()
    this.clearReturnAfterPickerHidden()
  }

  open(event) {
    event.preventDefault()
    event.stopPropagation()
    if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation()

    this.activeScope = event.currentTarget.closest("[data-exercise-picker-scope]")
    this.activeScopeId = this.ensureScopeId(this.activeScope)
    this.returnModalElement = event.currentTarget.closest(".modal")
    if (this.returnModalElement?.id === "exercise-picker-modal") {
      this.returnModalElement = null
    }

    this.registerReturnAfterPickerHidden()
    if (!this.loaded) this.load()
    this.refreshSelectionState()
    this.showPicker()
  }

  filter() {
    window.clearTimeout(this.debounceTimer)
    this.debounceTimer = window.setTimeout(() => this.load(), 250)
  }

  select(event) {
    event.preventDefault()
    const card = event.currentTarget.closest("[data-exercise-picker-target='item']")
    if (!card) return

    const input = this.activeTarget("input")
    const selectedName = this.activeTarget("selectedName")
    const selectedDetails = this.activeTarget("selectedDetails")

    if (!input) return

    input.value = card.dataset.exerciseId
    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.dispatchEvent(new Event("change", { bubbles: true }))

    if (selectedName) selectedName.textContent = card.dataset.name
    if (selectedDetails) selectedDetails.textContent = card.dataset.details || "Exercício selecionado"

    if (this.activeScope) {
      this.activeScope.classList.add("exercise-picker-scope--selected")
    }

    this.pickerItems.forEach((item) => item.classList.remove("is-selected"))
    card.classList.add("is-selected")
    this.refreshSelectionState()
    this.setStatus("Exercício selecionado. Voltando para a montagem do dia...")
    this.closePicker()
  }

  clearFilters() {
    if (this.hasQueryTarget) this.queryTarget.value = ""
    if (this.hasTypeTarget) this.typeTarget.value = ""
    if (this.hasDifficultyTarget) this.difficultyTarget.value = ""
    if (this.hasMuscleTarget) this.muscleTarget.value = ""
    if (this.hasEquipmentTarget) this.equipmentTarget.value = ""
    this.load()
  }

  preventSubmit(event) {
    event.preventDefault()
  }

  refreshSelectionState() {
    this.inputTargets.forEach((input) => {
      const scope = input.closest("[data-exercise-picker-scope]")
      const submit = scope ? this.findTarget(scope, "submit") : null

      if (submit) {
        submit.disabled = input.value.length === 0
      }
    })

    const activeInput = this.activeTarget("input")
    this.pickerItems.forEach((item) => {
      item.classList.toggle("is-selected", activeInput && item.dataset.exerciseId === activeInput.value)
    })
  }

  activeTarget(name) {
    const scope = this.currentActiveScope()
    if (!scope) return null

    return this.findTarget(scope, name)
  }

  findTarget(scope, name) {
    return scope.querySelector(`[data-exercise-picker-target~="${name}"]`)
  }

  get pickerItems() {
    return Array.from(this.element.querySelectorAll("[data-exercise-picker-target~='item']"))
  }

  get pickerModalElement() {
    return this.element.querySelector("#exercise-picker-modal")
  }

  showPicker() {
    if (!this.pickerModalElement) return

    const pickerModal = Modal.getOrCreateInstance(this.pickerModalElement)
    const returnModal =
      this.returnModalElement &&
      document.body.contains(this.returnModalElement) &&
      this.returnModalElement.classList.contains("show")

    if (!returnModal) {
      pickerModal.show()
      return
    }

    this.returnModalElement.addEventListener("hidden.bs.modal", () => pickerModal.show(), { once: true })
    Modal.getOrCreateInstance(this.returnModalElement).hide()
  }

  registerReturnAfterPickerHidden() {
    this.clearReturnAfterPickerHidden()
    if (!this.pickerModalElement || !this.returnModalElement) return

    this.returnAfterPickerHidden = () => {
      if (this.returnModalElement && document.body.contains(this.returnModalElement)) {
        Modal.getOrCreateInstance(this.returnModalElement).show()
      }
      this.returnModalElement = null
      this.returnAfterPickerHidden = null
    }

    this.pickerModalElement.addEventListener("hidden.bs.modal", this.returnAfterPickerHidden, { once: true })
  }

  clearReturnAfterPickerHidden() {
    if (this.pickerModalElement && this.returnAfterPickerHidden) {
      this.pickerModalElement.removeEventListener("hidden.bs.modal", this.returnAfterPickerHidden)
    }

    this.returnAfterPickerHidden = null
  }

  closePicker() {
    if (!this.pickerModalElement) return

    const pickerModal = Modal.getOrCreateInstance(this.pickerModalElement)

    if (this.pickerModalElement.classList.contains("show")) {
      pickerModal.hide()
    } else if (this.returnModalElement && document.body.contains(this.returnModalElement)) {
      Modal.getOrCreateInstance(this.returnModalElement).show()
      this.returnModalElement = null
    }
  }

  ensureScopeId(scope) {
    if (!scope) return null

    if (!scope.dataset.exercisePickerScopeId) {
      scope.dataset.exercisePickerScopeId = `exercise-picker-scope-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`
    }

    return scope.dataset.exercisePickerScopeId
  }

  currentActiveScope() {
    if (this.activeScope && document.body.contains(this.activeScope)) return this.activeScope
    if (!this.activeScopeId) return null

    this.activeScope = this.element.querySelector(
      `[data-exercise-picker-scope-id="${this.activeScopeId}"]`
    )

    return this.activeScope
  }

  normalize(value) {
    return (value || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
  }

  async load() {
    if (!this.hasUrlValue || !this.hasResultsTarget) return

    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()
    this.setStatus("Buscando exercícios...")

    try {
      const response = await fetch(this.urlWithParams(), {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        signal: this.abortController.signal
      })

      if (!response.ok) throw new Error("Não foi possível carregar o catálogo agora.")

      const payload = await response.json()
      this.loaded = true
      this.renderItems(payload.exercises || [], payload.meta || {})
      this.refreshSelectionState()
    } catch (error) {
      if (error.name === "AbortError") return

      this.renderItems([], { total: 0, limit: 0 })
      this.setStatus(error.message || "Não foi possível carregar o catálogo agora.", true)
    }
  }

  urlWithParams() {
    const url = new URL(this.urlValue, window.location.origin)
    const params = url.searchParams

    this.addParam(params, "query", this.hasQueryTarget ? this.queryTarget.value.trim() : "")
    this.addParam(params, "exercise_type", this.hasTypeTarget ? this.typeTarget.value : "")
    this.addParam(params, "difficulty", this.hasDifficultyTarget ? this.difficultyTarget.value : "")
    this.addParam(params, "muscle_group_id", this.hasMuscleTarget ? this.muscleTarget.value : "")
    this.addParam(params, "equipment_id", this.hasEquipmentTarget ? this.equipmentTarget.value : "")

    return url.toString()
  }

  addParam(params, name, value) {
    if (value) {
      params.set(name, value)
    } else {
      params.delete(name)
    }
  }

  renderItems(items, meta) {
    this.resultsTarget.innerHTML = items.map((exercise) => this.renderItem(exercise)).join("")

    if (this.hasCountTarget) {
      const total = Number(meta.total || items.length)
      const limit = Number(meta.limit || items.length)
      const suffix = total > limit ? ` de ${total}` : ""
      this.countTarget.textContent = `${items.length}${suffix} exercício${total === 1 ? "" : "s"}`
    }

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("d-none", items.length > 0)
    }

    this.setStatus(
      items.length > 0
        ? "Use a busca e os filtros para refinar o catálogo."
        : "Nenhum exercício encontrado para esses filtros."
    )
  }

  renderItem(exercise) {
    const muscles = exercise.muscles || []
    const equipments = exercise.equipments || []

    return `
      <div class="col-12 col-md-6 col-xxl-4">
        <button
          type="button"
          class="exercise-picker-card w-100 text-start"
          data-exercise-picker-target="item"
          data-action="click->exercise-picker#select"
          data-exercise-id="${this.escapeAttribute(exercise.id)}"
          data-name="${this.escapeAttribute(exercise.name)}"
          data-details="${this.escapeAttribute(exercise.details)}"
        >
          ${this.renderMedia(exercise)}

          <div class="exercise-picker-card__body">
            <div class="d-flex justify-content-between gap-2 mb-2">
              <div class="fw-semibold text-dark">${this.escapeHtml(exercise.name)}</div>
              <i class="bi bi-check-circle-fill exercise-picker-card__check"></i>
            </div>

            <div class="d-flex flex-wrap gap-1 mb-2">
              <span class="badge text-bg-light border">${this.escapeHtml(exercise.type_label)}</span>
              <span class="badge text-bg-light border">${this.escapeHtml(exercise.difficulty_label)}</span>
            </div>

            ${this.renderDetailLine("bi-bullseye", muscles.slice(0, 3).join(", "))}
            ${this.renderDetailLine("bi-tools", equipments.slice(0, 3).join(", "))}
          </div>
        </button>
      </div>
    `
  }

  renderMedia(exercise) {
    if (exercise.thumbnail_url) {
      return `
        <div class="exercise-picker-card__media">
          <img
            src="${this.escapeAttribute(exercise.thumbnail_url)}"
            alt="${this.escapeAttribute(exercise.name)}"
            class="exercise-picker-card__image"
            loading="lazy"
          >
        </div>
      `
    }

    return `
      <div class="exercise-picker-card__media">
        <div class="exercise-picker-card__placeholder">
          <i class="bi bi-activity"></i>
        </div>
      </div>
    `
  }

  renderDetailLine(icon, value) {
    if (!value) return ""

    return `
      <div class="small text-muted text-truncate">
        <i class="bi ${icon} me-1"></i>
        ${this.escapeHtml(value)}
      </div>
    `
  }

  setStatus(message, isError = false) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle("text-danger", isError)
    this.statusTarget.classList.toggle("text-muted", !isError)
  }

  escapeAttribute(value) {
    return this.escapeHtml(value)
  }

  escapeHtml(value) {
    return String(value ?? "").replace(/[&<>"']/g, (character) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "\"": "&quot;",
      "'": "&#39;"
    })[character])
  }
}
